<script setup lang="ts">
import { storeToRefs } from "pinia"
import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { useI18nStore } from "@/stores/i18n"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { useUserStore } from "@/stores/user"

const i18nStore = useI18nStore()

const { currentLocale, currentLang, languagesList, showLang } =
  storeToRefs(i18nStore)
const { updateLocale, changeAppLocale } = i18nStore
const { user } = storeToRefs(useUserStore())

const itemIsActive = (id: number) => currentLocale?.value.id === id

function updateAuthLocale(localeId: number) {
  // selection.value = localeId
  MixpanelInstance.track("language_updated", {
    language_previous: currentLocale.value.iso_code,
    language: languagesList.value.find((lang) => lang.id === localeId)
      ?.iso_code,
  })

  // cst_na user cannot update his locale, only change the app language
  if (user.value?.permissions?.includes("cst_na")) {
    changeAppLocale(localeId)
  } else {
    updateLocale(localeId)
  }
}

function openStatusChanged() {
  MixpanelInstance.track("language-menu_viewed")
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button
        @click="openStatusChanged"
        :disabled="!showLang"
        :aria-label="$t('language.search.placeholder')"
        variant="tertiary"
        :class="[
          'border-grey-lighten-3! text-grey h-11 w-auto space-x-2 rounded-full px-4 py-3',
          currentLocale.iso_code_639 === 'ar-SA' && 'space-x-reverse',
        ]"
      >
        <FaIcon icon="fas fa-earth-africa" class="h-5.25 w-5.25" />
        <span class="text-base font-light uppercase">
          {{ currentLang }}
        </span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      :ariaLabel="$t('accessibility.list.language')"
      class="mx-2 max-h-96 overflow-y-auto"
      v-if="showLang"
    >
      <DropdownMenuItem
        v-for="lang in languagesList"
        :key="lang.id"
        :aria-checked="itemIsActive(lang.id)"
        role="menuitemradio"
        variant="outline"
        class="border-afbranding-border focus-visible:ring-ring h-auto space-x-2 bg-white px-3 py-2 text-gray-500 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden focus-visible:ring-inset"
        @click="updateAuthLocale(lang.id)"
      >
        <span
          :class="
            cn('text-md font-normal', itemIsActive(lang.id) && 'font-bold')
          "
          >{{ lang.title }}</span
        >
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
